import React, { useEffect, useState } from "react";
import { api, PARTY_COLORS, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Users } from "lucide-react";

export default function AdminElections() {
  const [elections, setElections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [candidates, setCandidates] = useState([]);

  const [form, setForm] = useState({ name: "", election_type: "national", election_date: "", description: "", status: "draft" });
  const [candForm, setCandForm] = useState({ name: "", party: "", party_code: "", color: PARTY_COLORS[0], photo_url: "", party_symbol: "" });

  const load = async () => {
    const { data } = await api.get("/elections");
    setElections(data);
    if (data.length && !selected) setSelected(data[0]);
  };
  const loadCand = async (id) => {
    if (!id) return setCandidates([]);
    const { data } = await api.get(`/elections/${id}/candidates`);
    setCandidates(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadCand(selected?.id); }, [selected?.id]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/elections", form);
      toast.success("Election created");
      setForm({ name: "", election_type: "national", election_date: "", description: "", status: "draft" });
      await load(); setSelected(data);
    } catch (e2) { toast.error(formatDetail(e2.response?.data?.detail)); }
  };
  const updateStatus = async (status) => {
    try {
      await api.patch(`/elections/${selected.id}`, { ...selected, status });
      toast.success(`Election is now ${status}`);
      await load(); setSelected({ ...selected, status });
    } catch (e2) { toast.error(formatDetail(e2.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this election? All candidates & results will be removed.")) return;
    await api.delete(`/elections/${id}`);
    toast.success("Deleted");
    setSelected(null); load();
  };
  const addCand = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/elections/${selected.id}/candidates`, candForm);
      toast.success("Candidate added");
      setCandForm({ name: "", party: "", party_code: "", color: PARTY_COLORS[(candidates.length+1) % PARTY_COLORS.length], photo_url: "", party_symbol: "" });
      loadCand(selected.id);
    } catch (e2) { toast.error(formatDetail(e2.response?.data?.detail)); }
  };
  const removeCand = async (id) => {
    await api.delete(`/candidates/${id}`);
    loadCand(selected.id);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px]">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Configuration</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="elections-title">Elections</h1>

      <div className="grid grid-cols-12 gap-6 mt-8">
        {/* left: list + create */}
        <div className="col-span-12 lg:col-span-5 space-y-6">
          <form onSubmit={create} className="border border-white/10 bg-[#111114] p-5 space-y-3" data-testid="create-election-form">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2"><Plus size={12}/> New Election</div>
            <input required placeholder="Name (e.g. 2026 Presidential)" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-white outline-none" data-testid="election-name-input"/>
            <div className="grid grid-cols-2 gap-3">
              <select value={form.election_type} onChange={(e)=>setForm({...form, election_type: e.target.value})} className="bg-transparent border border-white/20 px-3 py-2 outline-none text-sm" data-testid="election-type-select">
                <option value="national" className="bg-black">National</option>
                <option value="state" className="bg-black">State / Regional</option>
                <option value="local" className="bg-black">Local / Municipal</option>
                <option value="custom" className="bg-black">Custom</option>
              </select>
              <input required type="date" value={form.election_date} onChange={(e)=>setForm({...form, election_date: e.target.value})} className="bg-transparent border border-white/20 px-3 py-2 outline-none text-sm [color-scheme:dark]" data-testid="election-date-input"/>
            </div>
            <textarea placeholder="Description (optional)" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} rows={2} className="w-full bg-transparent border border-white/20 p-3 outline-none text-sm resize-none"/>
            <button type="submit" className="w-full py-2.5 bg-white text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-zinc-200" data-testid="create-election-btn">Create Election</button>
          </form>

          <div className="border border-white/10">
            <div className="px-4 py-3 border-b border-white/10 text-xs uppercase tracking-[0.3em] text-zinc-500">All Elections</div>
            <div className="divide-y divide-white/5">
              {elections.map(e => (
                <button key={e.id} onClick={()=>setSelected(e)} data-testid={`election-row-${e.id}`}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/5 ${selected?.id===e.id?"bg-white/5":""}`}>
                  <div className={`w-1.5 h-8 ${e.status === "active" ? "bg-emerald-500" : e.status === "closed" ? "bg-zinc-600" : "bg-amber-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{e.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{e.election_type} · {e.status}</div>
                  </div>
                </button>
              ))}
              {!elections.length && <div className="p-6 text-zinc-500 text-sm">No elections.</div>}
            </div>
          </div>
        </div>

        {/* right: detail + candidates */}
        <div className="col-span-12 lg:col-span-7">
          {selected ? (
            <div className="space-y-6">
              <div className="border border-white/10 bg-[#111114] p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{selected.election_type}</div>
                    <h2 className="font-display text-3xl mt-1">{selected.name}</h2>
                    <div className="text-xs text-zinc-500 mt-1">{selected.election_date}</div>
                  </div>
                  <button onClick={()=>remove(selected.id)} className="p-2 border border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="delete-election-btn"><Trash2 size={14}/></button>
                </div>
                <div className="mt-4 flex gap-2">
                  {["draft","active","closed"].map(s => (
                    <button key={s} onClick={()=>updateStatus(s)} data-testid={`status-${s}-btn`}
                      className={`px-3 py-1.5 text-xs uppercase tracking-[0.2em] border ${selected.status===s?"bg-white text-black border-white":"border-white/20 hover:bg-white/10"}`}>{s}</button>
                  ))}
                </div>
              </div>

              <form onSubmit={addCand} className="border border-white/10 bg-[#111114] p-5 space-y-3" data-testid="add-candidate-form">
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2"><Users size={12}/> Add Candidate</div>
                <div className="grid grid-cols-2 gap-3">
                  <input required placeholder="Candidate name" value={candForm.name} onChange={(e)=>setCandForm({...candForm, name: e.target.value})} className="bg-transparent border-b border-white/20 py-2 outline-none focus:border-white" data-testid="candidate-name-input"/>
                  <input required placeholder="Party (e.g. Volt Party)" value={candForm.party} onChange={(e)=>setCandForm({...candForm, party: e.target.value})} className="bg-transparent border-b border-white/20 py-2 outline-none focus:border-white" data-testid="candidate-party-input"/>
                  <input required placeholder="Party code (e.g. VP)" value={candForm.party_code} onChange={(e)=>setCandForm({...candForm, party_code: e.target.value.toUpperCase()})} className="bg-transparent border-b border-white/20 py-2 outline-none focus:border-white" data-testid="candidate-code-input"/>
                  <div className="flex items-center gap-2 flex-wrap">
                    {PARTY_COLORS.map(c => (
                      <button type="button" key={c} onClick={()=>setCandForm({...candForm, color: c})}
                        className={`w-6 h-6 border-2 ${candForm.color===c?"border-white":"border-transparent"}`} style={{ background: c }} />
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 flex items-center gap-2 flex-wrap">
                    <label className="flex-1 min-w-[180px] cursor-pointer border border-white/20 hover:bg-white/10 px-3 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-2" data-testid="upload-photo-label">
                      <input type="file" accept="image/*" className="hidden" data-testid="candidate-photo-file" onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        try {
                          const fd = new FormData(); fd.append("file", f);
                          const { data } = await api.post("/upload?is_public=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/photo/${data.path}`;
                          setCandForm(f => ({ ...f, photo_url: url }));
                          toast.success("Photo uploaded");
                        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
                      }}/>
                      📸 {candForm.photo_url ? "Replace photo" : "Upload photo"}
                    </label>
                    <button type="button" onClick={async () => {
                      if (!candForm.name) return toast.error("Enter candidate name first");
                      if (!confirm(`Generate an AI portrait for ${candForm.name}? The result will be watermarked "AI GENERATED".`)) return;
                      try {
                        toast.info("Generating AI portrait — 10-20 seconds...");
                        const { data } = await api.post("/ai/candidate-portrait", { prompt: `Portrait of ${candForm.name}, ${candForm.party ? "politician of " + candForm.party : "professional politician"}` });
                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/photo/${data.path}`;
                        setCandForm(f => ({ ...f, photo_url: url }));
                        toast.success("AI portrait ready");
                      } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "AI generation not available"); }
                    }} className="border border-purple-400/50 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 px-3 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-1" data-testid="ai-portrait-btn" title="Admin-only. Generated images are watermarked 'AI GENERATED'.">
                      ✨ AI Portrait
                    </button>
                    {candForm.photo_url && <img src={candForm.photo_url} alt="preview" className="w-10 h-10 object-cover border-2" style={{ borderColor: candForm.color }}/>}
                  </div>
                  <input placeholder="Party symbol — emoji or image URL" value={candForm.party_symbol} onChange={(e)=>setCandForm({...candForm, party_symbol: e.target.value})} className="col-span-2 bg-transparent border-b border-white/20 py-2 outline-none focus:border-white text-sm" data-testid="candidate-symbol-input"/>
                </div>
                <button type="submit" className="px-4 py-2 bg-white text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-zinc-200" data-testid="add-candidate-btn">Add Candidate</button>
              </form>

              <div className="border border-white/10">
                <div className="px-4 py-3 border-b border-white/10 text-xs uppercase tracking-[0.3em] text-zinc-500">Candidates ({candidates.length})</div>
                <div className="divide-y divide-white/5">
                  {candidates.map(c => (
                    <div key={c.id} className="px-4 py-3 flex items-center gap-3">
                      {c.photo_url ? (
                        <img src={c.photo_url} alt={c.name} className="w-10 h-10 object-cover border-2" style={{ borderColor: c.color }}/>
                      ) : (
                        <div className="w-1.5 h-10" style={{ background: c.color }}/>
                      )}
                      <div className="flex-1">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                          {c.party_symbol && (c.party_symbol.startsWith("http") ? <img src={c.party_symbol} alt="" className="w-3.5 h-3.5"/> : <span>{c.party_symbol}</span>)}
                          <span>{c.party} · {c.party_code}</span>
                        </div>
                      </div>
                      <button onClick={()=>removeCand(c.id)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`delete-candidate-${c.id}`}><Trash2 size={14}/></button>
                    </div>
                  ))}
                  {!candidates.length && <div className="p-6 text-zinc-500 text-sm">No candidates yet.</div>}
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-white/10 p-12 text-center text-zinc-500">Select or create an election</div>
          )}
        </div>
      </div>
    </div>
  );
}
